import {ModuleProto} from "../module-proto";
import {ModuleType} from "../types";
import {SettingsMenuTopCategory} from "../settings-module";
import {SettingsParentSection} from "../../../app/settings/const";

import {ValidationAction, ValidationCondition, ValidationTrigger} from "./@const";
import {SettingsSection, SettingsSectionTitleDict} from "../../../app/settings/@const";
import ValidationCollection from "../../model2/validation/validations";
import {ValidationJson} from "../../model2/validation/@types";
import {ModuleValidations_Access} from "./access/acl";

// const debug = "ModuleValidations"
const debug = false

export class ModuleValidations extends ModuleProto {

    public dependencyOnClose                        : ValidationAction
    public dependencyOnStartEndDateUpdate           : ValidationAction

    public subtaskOnClose                           : ValidationAction
    public subtaskOnStartEndDateUpdate              : ValidationAction

    public taskScheduleDateBeforeStartDate          : ValidationAction
    public taskScheduleDateAfterEndDate             : ValidationAction
    public taskScheduleDateAfterDeadline            : ValidationAction
    public openTaskInClosedProject                  : ValidationAction

    public validations                              : ValidationCollection;

    constructor(moduleConfig: JSONObject) {
        super(ModuleType.VALIDATIONS, moduleConfig);

        debug && console.log(debug, "constructor() moduleConfig=", moduleConfig)

        this.name                       = "Validations"

        this.description                = "The Validations Module ensures data integrity by preventing actions that violate predefined rules, such as archiving items with incomplete dependencies, breaching planning rules, or any other system or user-defined validations."
        this.svgIcon                    = "isvg-validations"
        this.settingsMenuTopCategory    = SettingsMenuTopCategory.ORGANIZATION
        this.settingsSection            = SettingsSection.AUTOMATIONS
        this.typeLabel                  = SettingsSectionTitleDict[SettingsSection.AUTOMATIONS]
        this.settingsUrl                = "settings/validations"
        this.parentSection              = SettingsParentSection.AUTOMATIONS
        this.canDisable                 = false

        // users custom validations
        this.validations               = ValidationCollection.initFromJson(
            _.get(moduleConfig, 'validations', []) as ValidationJson[]
        )

        // system validations
        this.dependencyOnClose                          = _.get(moduleConfig, 'dependencyOnClose', ValidationAction.WARNING) as ValidationAction
        this.dependencyOnStartEndDateUpdate             = _.get(moduleConfig, 'dependencyOnStartEndDateUpdate', ValidationAction.ALLOW) as ValidationAction

        this.subtaskOnClose                             = _.get(moduleConfig, 'subtaskOnClose', ValidationAction.WARNING) as ValidationAction
        this.subtaskOnStartEndDateUpdate                = _.get(moduleConfig, 'subtaskOnStartEndDateUpdate', ValidationAction.ALLOW) as ValidationAction

        this.taskScheduleDateBeforeStartDate            = _.get(moduleConfig, 'taskScheduleDateBeforeStartDate', ValidationAction.ALLOW) as ValidationAction
        this.taskScheduleDateAfterEndDate               = _.get(moduleConfig, 'taskScheduleDateAfterEndDate', ValidationAction.ALLOW) as ValidationAction
        this.taskScheduleDateAfterDeadline              = _.get(moduleConfig, 'taskScheduleDateAfterDeadline', ValidationAction.WARNING) as ValidationAction

        // cannot be changed
        this.openTaskInClosedProject                    = _.get(moduleConfig, 'taskOnOpen', ValidationAction.DENY) as ValidationAction
    }

    get acl():ModuleValidations_Access {
        return new ModuleValidations_Access()
    }


    get shouldCheckDependencyOnClose():boolean {
        return this.dependencyOnClose === ValidationAction.DENY || this.dependencyOnClose === ValidationAction.WARNING
    }
    get shouldCheckSubtaskOnClose():boolean {
        return this.subtaskOnClose === ValidationAction.DENY || this.subtaskOnClose === ValidationAction.WARNING
    }

    get shouldCheckDependencyOnStartEndDateUpdate():boolean {
        return this.dependencyOnStartEndDateUpdate !== ValidationAction.ALLOW
    }
    get shouldCheckSubtaskOnStartEndDateUpdate():boolean {
        return this.subtaskOnStartEndDateUpdate !== ValidationAction.ALLOW
    }

    get shouldCheckTaskScheduleDateBeforeStartDate():boolean {
        return this.taskScheduleDateBeforeStartDate !== ValidationAction.ALLOW
    }
    get shouldCheckTaskScheduleDateAfterEndDate():boolean {
        return this.taskScheduleDateAfterEndDate !== ValidationAction.ALLOW
    }
    get shouldCheckTaskScheduleDateAfterDeadline():boolean {
        return this.taskScheduleDateAfterDeadline !== ValidationAction.ALLOW
    }

    get shouldTaskOnOpen():boolean {
        return true
    }


    // Update payload methods
    updateRequestPayloadOnScheduleChange(requestPayload:any): void {

        if (!requestPayload.gdValidations) {
            requestPayload.gdValidations = []
        }

        if (this.shouldCheckTaskScheduleDateBeforeStartDate){
            requestPayload.gdValidations.push({
                id			: 'system-task-schedule-before-start-date',
                trigger		: ValidationTrigger.TASK_SCHEDULE_DATE_UPDATE,
                condition	: {
                    type: ValidationCondition.TASK_SCHEDULE_DATE_BEFORE_START_DATE
                },
                isSystem    : true,
                action		: this.taskScheduleDateBeforeStartDate
            })
        }
        if (this.shouldCheckTaskScheduleDateAfterEndDate){
            requestPayload.gdValidations.push({
                id			: 'system-task-schedule-after-end-date',
                trigger		: ValidationTrigger.TASK_SCHEDULE_DATE_UPDATE,
                condition	: {
                    type: ValidationCondition.TASK_SCHEDULE_DATE_AFTER_END_DATE
                },
                isSystem    : true,
                action		: this.taskScheduleDateAfterEndDate
            })
        }
        if (this.shouldCheckTaskScheduleDateAfterDeadline){
            requestPayload.gdValidations.push({
                id			: 'system-task-schedule-after-deadline',
                trigger		: ValidationTrigger.TASK_SCHEDULE_DATE_UPDATE,
                condition	: {
                    type: ValidationCondition.TASK_SCHEDULE_DATE_AFTER_DEADLINE
                },
                isSystem    : true,
                action		: this.taskScheduleDateAfterDeadline
            })
        }
        // requestPayload['validations'] = this.validations.enabledValidations()
    }

    updateRequestPayloadOnTaskStatusChange(requestPayload:any): void {

        if (!requestPayload.gdValidations) {
            requestPayload.gdValidations = []
        }

        if (this.shouldCheckDependencyOnClose){
            requestPayload.gdValidations.push({
                id			: 'system-open-dependency',
                trigger		: ValidationTrigger.TASK_CLOSE,
                condition	: {
                    type: ValidationCondition.HAS_OPEN_DEPENDENCY
                },
                isSystem    : true,
                action		: this.dependencyOnClose
            })
        }

        if (this.shouldCheckSubtaskOnClose){
            requestPayload.gdValidations.push({
                id			: 'system-open-subtask',
                trigger		: ValidationTrigger.TASK_CLOSE,
                condition	: {
                    type: ValidationCondition.HAS_OPEN_SUBTASKS
                },
                isSystem    : true,
                action		: this.subtaskOnClose
            })
        }

        if (this.shouldTaskOnOpen){
            requestPayload.gdValidations.push({
                id			: 'system-closed-project',
                trigger		: ValidationTrigger.TASK_ON_OPEN,
                condition	: {
                    type: ValidationCondition.PROJECT_CLOSED
                },
                isSystem    : true,
                action		: this.openTaskInClosedProject
            })
        }

    }

    updateRequestPayloadOnTaskStartEndDateChange(requestPayload:any): void {
        debug && console.log(debug, "updateRequestPayloadOnTaskStartEndDateChange() START", "requestPayload=", requestPayload)
        if (!requestPayload.gdValidations) {
            requestPayload.gdValidations = []
        }

        if (this.shouldCheckDependencyOnStartEndDateUpdate){
            requestPayload.gdValidations.push({
                id			: 'system-dependency-on-start-end-date-change',
                trigger		: ValidationTrigger.TASK_START_END_DATE_UPDATE,
                condition	: {
                    type: ValidationCondition.TASK_DEPENDENCY_START_END_DATE_CONFLICTS
                },
                isSystem    : true,
                action		: this.dependencyOnStartEndDateUpdate
            })
        }
        if (this.shouldCheckSubtaskOnStartEndDateUpdate){
            requestPayload.gdValidations.push({
                id			: 'system-subtask-on-start-end-date-change',
                trigger		: ValidationTrigger.TASK_START_END_DATE_UPDATE,
                condition	: {
                    type: ValidationCondition.TASK_SUBTASK_START_END_DATE_CONFLICTS
                },
                isSystem    : true,
                action		: this.subtaskOnStartEndDateUpdate
            })
        }

        debug && console.log(debug, "updateRequestPayloadOnTaskStartEndDateChange() END", "requestPayload=", requestPayload)



    }

    toJson()  {
        const result = super.toJson();

        result.validations  = this.validations.toJson()

        result.dependencyOnClose                        = this.dependencyOnClose
        result.dependencyOnStartEndDateUpdate           = this.dependencyOnStartEndDateUpdate

        result.subtaskOnClose                           = this.subtaskOnClose
        result.subtaskOnStartEndDateUpdate              = this.subtaskOnStartEndDateUpdate

        result.taskScheduleDateBeforeStartDate          = this.taskScheduleDateBeforeStartDate
        result.taskScheduleDateAfterEndDate             = this.taskScheduleDateAfterEndDate
        result.taskScheduleDateAfterDeadline            = this.taskScheduleDateAfterDeadline

        return result
    }

}
